import React, { useEffect, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { createPortal } from 'react-dom'
import {
    SparklesIcon,
    ArrowLeftIcon,
    ChevronLeftIcon,
    ChevronRightIcon
} from '@heroicons/react/24/solid'
import Footer from '../components/Footer'

interface FashionItem {
    type: 'top' | 'bottom';
    brand: string;
    name: string;
    price: string;
    original_price?: string;
    last_lowest_price?: string;
    image: string;
    tags: string[];
    similarity: number;
}

interface Accessory {
    brand: string;
    name: string;
    price: string;
    image: string;
}

interface Recommendations {
    title: string;
    description: string;
    accessories: Accessory[];
}

interface StyleData {
    set_name: string;
    description: string;
    items: {
        top: Omit<FashionItem, 'type'> | Omit<FashionItem, 'type'>[];
        bottom?: Omit<FashionItem, 'type'> | Omit<FashionItem, 'type'>[];
    };
    recommendations?: Recommendations;
}

interface ItemGroupState {
    items: FashionItem[];
    currentIndex: number;
}

const StylePage: React.FC = () => {
    const { styleId } = useParams<{ styleId: string }>()
    const [itemGroups, setItemGroups] = useState<Record<string, ItemGroupState>>({})
    const [videoSrc, setVideoSrc] = useState('')
    const [setInfo, setSetInfo] = useState<{ name: string; description: string } | null>(null)
    const [recommendations, setRecommendations] = useState<Recommendations | null>(null)
    const scrollContainerRef = React.useRef<HTMLDivElement>(null)
    const [canScrollLeft, setCanScrollLeft] = useState(false)
    const [canScrollRight, setCanScrollRight] = useState(false)
    const [activeTooltip, setActiveTooltip] = useState<{ accessory: Accessory; rect: DOMRect } | null>(null)

    // Add scroll position check
    const checkScrollPosition = () => {
        const container = scrollContainerRef.current
        if (container) {
            const hasHorizontalScroll = container.scrollWidth > container.clientWidth
            setCanScrollLeft(container.scrollLeft > 0)
            setCanScrollRight(hasHorizontalScroll && container.scrollLeft < (container.scrollWidth - container.clientWidth))
        }
    }

    // Add scroll event listener and resize observer
    useEffect(() => {
        const container = scrollContainerRef.current
        if (container) {
            container.addEventListener('scroll', checkScrollPosition)

            // Check on mount and when content changes
            checkScrollPosition()

            // Create resize observer to check when container size changes
            const resizeObserver = new ResizeObserver(checkScrollPosition)
            resizeObserver.observe(container)

            return () => {
                container.removeEventListener('scroll', checkScrollPosition)
                resizeObserver.disconnect()
            }
        }
    }, [recommendations]) // Re-run when recommendations change

    useEffect(() => {
        const loadStyleData = async () => {
            if (styleId) {
                try {
                    const response = await fetch(`/fashion_sets/${styleId}/src.txt`)
                    const data: StyleData = JSON.parse(await response.text())

                    setVideoSrc(`/fashion_sets/${styleId}/video.mp4`)
                    setSetInfo({
                        name: data.set_name,
                        description: data.description
                    })

                    if (data.recommendations) {
                        setRecommendations(data.recommendations)
                    }

                    const newItemGroups: Record<string, ItemGroupState> = {
                        top: {
                            items: Array.isArray(data.items.top)
                                ? data.items.top.map(item => ({ type: 'top' as const, ...item }))
                                : [{ type: 'top' as const, ...data.items.top }],
                            currentIndex: 0
                        }
                    }

                    if (data.items.bottom) {
                        newItemGroups.bottom = {
                            items: Array.isArray(data.items.bottom)
                                ? data.items.bottom.map(item => ({ type: 'bottom' as const, ...item }))
                                : [{ type: 'bottom' as const, ...data.items.bottom }],
                            currentIndex: 0
                        }
                    }

                    setItemGroups(newItemGroups)
                } catch (error) {
                    console.error('Error loading style data:', error)
                }
            }
        }

        loadStyleData()
    }, [styleId])

    const handlePrevItem = (type: string) => {
        setItemGroups(prev => ({
            ...prev,
            [type]: {
                ...prev[type],
                currentIndex: Math.max(0, prev[type].currentIndex - 1)
            }
        }))
    }

    const handleNextItem = (type: string) => {
        setItemGroups(prev => ({
            ...prev,
            [type]: {
                ...prev[type],
                currentIndex: Math.min(prev[type].items.length - 1, prev[type].currentIndex + 1)
            }
        }))
    }

    const NavigationButton: React.FC<{
        direction: 'left' | 'right';
        onClick: () => void;
        disabled: boolean;
    }> = ({ direction, onClick, disabled }) => (
        <button
            onClick={onClick}
            disabled={disabled}
            className={`absolute top-1/2 -translate-y-1/2 ${direction === 'left' ? '-left-3' : '-right-3'}
                       bg-white/80 backdrop-blur-[2px] rounded-full p-1.5
                       transition-all duration-200
                       ${disabled ? 'opacity-0 cursor-default pointer-events-none' : 'hover:bg-white hover:scale-105 active:scale-95'}`}
            aria-label={`Show ${direction === 'left' ? 'previous' : 'next'} item`}
        >
            {direction === 'left' ? (
                <ChevronLeftIcon className="w-4 h-4 text-gray-600" />
            ) : (
                <ChevronRightIcon className="w-4 h-4 text-gray-600" />
            )}
        </button>
    )

    const handleScroll = (direction: 'left' | 'right') => {
        const container = scrollContainerRef.current
        if (container) {
            const scrollAmount = 200
            const targetScroll = direction === 'left'
                ? container.scrollLeft - scrollAmount
                : container.scrollLeft + scrollAmount

            container.scrollTo({
                left: targetScroll,
                behavior: 'smooth'
            })
        }
    }

    // Add this function to handle tooltip positioning
    const handleMouseEnter = (e: React.MouseEvent<HTMLDivElement>, accessory: Accessory) => {
        const rect = e.currentTarget.getBoundingClientRect()
        setActiveTooltip({ accessory, rect })
    }

    const handleMouseLeave = () => {
        setActiveTooltip(null)
    }

    return (
        <div className="min-h-screen flex flex-col bg-white scrollbar-hide">
            {/* Navigation */}
            <nav className="py-3 border-b border-gray-100">
                <div className="max-w-[1200px] mx-auto px-6">
                    <Link
                        to="/"
                        className="inline-flex items-center text-gray-600 hover:text-gray-900 transition-colors"
                    >
                        <ArrowLeftIcon className="w-4 h-4 mr-2" />
                        <span className="text-sm">Back to all styles</span>
                    </Link>
                </div>
            </nav>

            <main className="flex-1 max-w-[1200px] mx-auto px-6 flex items-center">
                <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr),520px] gap-10 w-full">
                    {/* Video Section */}
                    <div className="relative flex items-center justify-center group">
                        <div className="w-full flex items-center justify-center">
                            <video
                                className="max-w-full max-h-[calc(100vh-180px)] w-auto h-auto object-contain"
                                style={{ pointerEvents: 'none' }}
                                src={videoSrc}
                                autoPlay
                                loop
                                muted
                                playsInline
                            />
                            {/* Hover tooltip */}
                            {setInfo && (
                                <div className="absolute bottom-4 inset-x-0 mx-auto p-6 bg-gray-900/95 rounded-lg 
                                    opacity-0 group-hover:opacity-100 transition-all duration-200 z-10 shadow-xl">
                                    <h2 className="text-xl font-medium text-white mb-3">
                                        {setInfo.name}
                                    </h2>
                                    <p className="text-base text-gray-200 leading-relaxed">
                                        {setInfo.description}
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Right Panel */}
                    <div className="flex flex-col h-full">
                        {/* Items Section */}
                        <div className="flex-1">
                            <div className="flex items-center gap-2 mb-6">
                                <h1 className="text-xl text-gray-900">Get This Look</h1>
                                <div className="relative group">
                                    <div className="bg-blue-600 rounded-md px-2 py-0.5 flex items-center gap-1 cursor-help">
                                        <SparklesIcon className="w-3 h-3 text-white" />
                                        <span className="text-xs text-white">AI-Powered</span>
                                    </div>
                                    {/* Tooltip */}
                                    <div className="absolute left-0 top-full mt-2 w-64 p-2 bg-gray-900 rounded-lg text-xs text-white opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-10">
                                        <div className="absolute -top-1 left-4 w-2 h-2 bg-gray-900 rotate-45" />
                                        Our AI analyzes the style in the video and finds similar items you can buy. The percentage shows how closely each item matches the original look.
                                    </div>
                                </div>
                            </div>

                            <div className="space-y-4">
                                {Object.entries(itemGroups).map(([type, group]) => {
                                    const currentItem = group.items[group.currentIndex]
                                    return (
                                        <div key={type} className="relative">
                                            <div className="flex gap-4">
                                                <div className="relative w-[80px] flex-shrink-0">
                                                    <div className="relative bg-gray-50 rounded-t w-full pb-[133%] mb-0.5">
                                                        <img
                                                            src={`/fashion_sets/${styleId}/${currentItem.image}`}
                                                            alt={`${currentItem.type} item`}
                                                            className="absolute inset-0 w-full h-full object-contain"
                                                        />

                                                        {group.items.length > 1 && (
                                                            <>
                                                                <NavigationButton
                                                                    direction="left"
                                                                    onClick={() => handlePrevItem(type)}
                                                                    disabled={group.currentIndex === 0}
                                                                />
                                                                <NavigationButton
                                                                    direction="right"
                                                                    onClick={() => handleNextItem(type)}
                                                                    disabled={group.currentIndex === group.items.length - 1}
                                                                />
                                                            </>
                                                        )}
                                                    </div>

                                                    {group.items.length > 1 && (
                                                        <div className="flex justify-center items-center gap-0.5">
                                                            {group.items.map((_, index) => (
                                                                <button
                                                                    key={index}
                                                                    onClick={() => {
                                                                        setItemGroups(prev => ({
                                                                            ...prev,
                                                                            [type]: {
                                                                                ...prev[type],
                                                                                currentIndex: index
                                                                            }
                                                                        }))
                                                                    }}
                                                                    className={`w-1 h-1 rounded-full transition-all duration-200
                                                                        ${index === group.currentIndex
                                                                            ? 'bg-blue-600'
                                                                            : 'bg-gray-300 hover:bg-gray-400'}`}
                                                                    aria-label={`Go to item ${index + 1}`}
                                                                />
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>

                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center justify-between mb-0.5">
                                                        <div className="text-sm text-gray-600">{currentItem.brand}</div>
                                                        <div className="flex items-center gap-1 text-blue-600">
                                                            <SparklesIcon className="w-3 h-3" />
                                                            <span className="text-sm font-medium">
                                                                {currentItem.similarity.toFixed(1)}%
                                                            </span>
                                                        </div>
                                                    </div>

                                                    <h3 className="text-base text-gray-900 mb-1">{currentItem.name}</h3>

                                                    <div className="h-[60px]">
                                                        <div className="text-base font-medium mb-1">
                                                            {currentItem.price} €
                                                        </div>
                                                        <div className="space-y-0.5">
                                                            {currentItem.original_price ? (
                                                                <div className="text-xs text-gray-500">
                                                                    Originally: {currentItem.original_price} €
                                                                </div>
                                                            ) : (
                                                                <div className="h-[16px]" />
                                                            )}
                                                            {currentItem.last_lowest_price ? (
                                                                <div className="text-xs text-gray-500">
                                                                    Last lowest: {currentItem.last_lowest_price} €
                                                                </div>
                                                            ) : (
                                                                <div className="h-[16px]" />
                                                            )}
                                                        </div>
                                                    </div>

                                                    <div className="flex flex-wrap gap-1">
                                                        {currentItem.tags.map(tag => (
                                                            <span
                                                                key={tag}
                                                                className="px-1.5 py-0.5 bg-gray-100 text-gray-600 text-xs rounded"
                                                            >
                                                                {tag}
                                                            </span>
                                                        ))}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    )
                                })}
                            </div>

                            {/* Recommendations Section */}
                            {recommendations && (
                                <div className="mt-6">
                                    <div className="border-t-2 border-gray-200 pt-4">
                                        <h2 className="text-base text-gray-900 mb-4">{recommendations.title}</h2>
                                        <div className="relative overflow-visible">
                                            <div className="overflow-x-auto overflow-y-visible scrollbar-hide" ref={scrollContainerRef}>
                                                <div className="flex gap-4 pb-4 pr-8 min-w-max overflow-visible scrollbar-hide">
                                                    {recommendations.accessories.map((accessory, index) => (
                                                        <div
                                                            key={index}
                                                            className="group relative flex-shrink-0 first:ml-0"
                                                            onMouseEnter={(e) => handleMouseEnter(e, accessory)}
                                                            onMouseLeave={handleMouseLeave}
                                                        >
                                                            <div className="bg-white rounded border border-gray-100 overflow-hidden hover:shadow-sm transition-all duration-200">
                                                                <div className="w-[90px]">
                                                                    <div className="relative pb-[100%] bg-gray-50">
                                                                        <img
                                                                            src={`/fashion_sets/${styleId}/${accessory.image}`}
                                                                            alt={accessory.name}
                                                                            className="absolute inset-0 w-full h-full object-contain p-3"
                                                                        />
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                            {/* Scroll Buttons */}
                                            {canScrollLeft && (
                                                <button
                                                    onClick={() => handleScroll('left')}
                                                    className="absolute left-0 top-0 h-[90px] bg-gradient-to-r from-white/90 via-white/50 to-transparent w-12 flex items-center justify-start hover:bg-gradient-to-r hover:from-white hover:via-white/60 hover:to-transparent transition-all duration-200"
                                                >
                                                    <ChevronLeftIcon className="w-6 h-6 text-gray-600 ml-1" />
                                                </button>
                                            )}
                                            {canScrollRight && (
                                                <button
                                                    onClick={() => handleScroll('right')}
                                                    className="absolute right-0 top-0 h-[90px] bg-gradient-to-l from-white/90 via-white/50 to-transparent w-12 flex items-center justify-end hover:bg-gradient-to-l hover:from-white hover:via-white/60 hover:to-transparent transition-all duration-200"
                                                >
                                                    <ChevronRightIcon className="w-6 h-6 text-gray-600 mr-1" />
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </main>
            <Footer />
            {activeTooltip && createPortal(
                <div
                    className="fixed transition-all duration-200 z-[9999] pointer-events-none"
                    style={{
                        left: activeTooltip.rect.left + (activeTooltip.rect.width / 2),
                        top: activeTooltip.rect.bottom + 8,
                        transform: 'translateX(-50%)',
                    }}
                >
                    <div className="relative bg-gray-900 rounded-lg p-3 text-xs text-white shadow-xl w-48">
                        <div className="absolute -top-1 left-1/2 -translate-x-1/2 w-2 h-2 bg-gray-900 rotate-45" />
                        <div className="relative z-10">
                            <div className="text-center">
                                <div className="font-medium mb-1">{activeTooltip.accessory.name}</div>
                                <div className="opacity-90">{activeTooltip.accessory.brand}</div>
                                <div className="mt-1 font-medium">{activeTooltip.accessory.price} €</div>
                            </div>
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </div>
    )
}

export default StylePage
